import type {
  CreateCustomModuleFieldInput,
  CreateCustomModuleInput,
  CreateCustomRecordInput,
  CustomFieldDefinitionSummary,
  CustomModuleDetail,
  CustomModuleSummary,
  CustomRecordDetail,
  CustomRecordSummary,
  Paginated,
  RecordRef,
  RelatedCustomList,
  UpdateCustomFieldInput,
  UpdateCustomModuleInput,
  UpdateCustomRecordInput,
} from '@whn/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, toQueryString } from '@/lib/api-client';

/**
 * Modules an administrator defined, and their records.
 *
 * One set of hooks for every custom module there will ever be — the module is
 * a parameter, not a code path.
 */

const KEY = {
  all: ['custom-modules'] as const,
  detail: (ref: string) => ['custom-modules', 'detail', ref] as const,
  records: (ref: string, params: Record<string, unknown>) =>
    ['custom-modules', ref, 'records', params] as const,
  record: (ref: string, id: string) => ['custom-modules', ref, 'record', id] as const,
  related: (target: string, id: string) => ['custom-modules', 'related', target, id] as const,
  options: (ref: string, search: string) => ['custom-modules', ref, 'options', search] as const,
};

/** Invalidates everything about custom modules: definitions and records. */
function invalidate(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: KEY.all });
}

export function useCustomModules() {
  return useQuery({
    queryKey: KEY.all,
    queryFn: ({ signal }) => api.get<CustomModuleSummary[]>('/custom-modules', signal),
    // The navigation reads this on every page, and modules change rarely.
    staleTime: 60_000,
  });
}

export function useCustomModule(ref: string | undefined) {
  return useQuery({
    queryKey: KEY.detail(ref ?? ''),
    queryFn: ({ signal }) => api.get<CustomModuleDetail>(`/custom-modules/${ref}`, signal),
    enabled: Boolean(ref),
  });
}

export function useCreateCustomModule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomModuleInput) =>
      api.post<CustomModuleDetail>('/custom-modules', input),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useUpdateCustomModule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCustomModuleInput }) =>
      api.patch<CustomModuleDetail>(`/custom-modules/${id}`, input),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useArchiveCustomModule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/custom-modules/${id}`),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useAddCustomModuleField(moduleRef: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomModuleFieldInput) =>
      api.post<CustomFieldDefinitionSummary>(`/custom-modules/${moduleRef}/fields`, input),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useUpdateCustomModuleField(moduleRef: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fieldId, input }: { fieldId: string; input: UpdateCustomFieldInput }) =>
      api.patch<CustomFieldDefinitionSummary>(
        `/custom-modules/${moduleRef}/fields/${fieldId}`,
        input,
      ),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useRemoveCustomModuleField(moduleRef: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fieldId: string) =>
      api.delete<void>(`/custom-modules/${moduleRef}/fields/${fieldId}`),
    onSuccess: () => invalidate(queryClient),
  });
}

// --- Records --------------------------------------------------------------

type Params = Record<string, string | number | undefined | null>;

export function useCustomRecords(moduleRef: string, params: Params) {
  return useQuery({
    queryKey: KEY.records(moduleRef, params),
    queryFn: ({ signal }) =>
      api.get<Paginated<CustomRecordSummary>>(
        `/custom-modules/${moduleRef}/records${toQueryString(params)}`,
        signal,
      ),
    enabled: Boolean(moduleRef),
  });
}

export function useCustomRecord(moduleRef: string, id: string | undefined) {
  return useQuery({
    queryKey: KEY.record(moduleRef, id ?? ''),
    queryFn: ({ signal }) =>
      api.get<CustomRecordDetail>(`/custom-modules/${moduleRef}/records/${id}`, signal),
    enabled: Boolean(moduleRef && id),
  });
}

export function useCustomRecordOptions(moduleRef: string | undefined, search: string) {
  return useQuery({
    queryKey: KEY.options(moduleRef ?? '', search),
    queryFn: ({ signal }) =>
      api.get<RecordRef[]>(
        `/custom-modules/${moduleRef}/records/options${toQueryString({ search })}`,
        signal,
      ),
    enabled: Boolean(moduleRef),
    staleTime: 30_000,
  });
}

export function useCreateCustomRecord(moduleRef: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomRecordInput) =>
      api.post<CustomRecordDetail>(`/custom-modules/${moduleRef}/records`, input),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useUpdateCustomRecord(moduleRef: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCustomRecordInput }) =>
      api.patch<CustomRecordDetail>(`/custom-modules/${moduleRef}/records/${id}`, input),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useArchiveCustomRecord(moduleRef: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/custom-modules/${moduleRef}/records/${id}`),
    onSuccess: () => invalidate(queryClient),
  });
}

/**
 * The custom modules that point at a record, for its related lists.
 *
 * `target` is a standard module name (`ACCOUNT`) or `custom:<moduleId>`.
 * Invalidated with everything else about custom modules, so creating a record
 * from a related list refreshes the count.
 */
export function useRelatedCustomLists(target: string, id: string | undefined) {
  return useQuery({
    queryKey: KEY.related(target, id ?? ''),
    queryFn: ({ signal }) =>
      api.get<RelatedCustomList[]>(`/custom-modules/related/${target}/${id}`, signal),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}
